import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyDocumentDirection,
  applyDocumentTitle,
  BarahWordmark,
  ProductBrand,
  SUPPORTED_LANGUAGES,
} from '@barahpay/shared';
import type { CheckoutMerchantInfo } from '@barahpay/shared';
import { setApiLanguage } from '../api';

interface CheckoutCardProps {
  merchant?: CheckoutMerchantInfo;
  children: ReactNode;
}

/**
 * The page frame: Barah Pay branding plus the merchant identity, the security indicator and the
 * language switch (spec section 18).
 */
export function CheckoutCard({ merchant, children }: CheckoutCardProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language.startsWith('ar');

  const merchantName = isArabic && merchant?.nameAr ? merchant.nameAr : merchant?.name;

  function switchLanguage(language: string) {
    void i18n.changeLanguage(language);
    applyDocumentDirection(language);
    applyDocumentTitle(t('common.appName'));
    setApiLanguage(language);
  }

  return (
    <div className="bp-shell">
      <div>
        {/* Barah Pay owns this page; the merchant identity sits inside the card, under it. */}
        <div className="bp-brandbar">
          {/* The same lockup Barah Plus and the admin portal carry: mark on a tile, name, Barah logo. */}
          <ProductBrand size="md" />
        </div>

        <div className="bp-card">
          <div className="bp-card__accent" />

          <div className="bp-card__header">
            {/*
              A merchant without a logo gets a monogram, never an empty grey square: a blank image
              slot reads as a broken page, and on a payment page any hint of breakage costs conversion.
            */}
            {merchant?.logoUrl ? (
              <img className="bp-card__logo" src={merchant.logoUrl} alt="" />
            ) : merchantName ? (
              <div className="bp-card__monogram" aria-hidden="true">
                {merchantName.trim().charAt(0).toLocaleUpperCase()}
              </div>
            ) : null}

            <div className="bp-card__identity">
              {/* The lockup above the card already names Barah Pay, so this line stays the merchant's. */}
              {merchantName ? <div className="bp-card__merchant">{merchantName}</div> : null}
            </div>

            <div className="bp-card__secure">
              <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
                <rect x="3" y="7" width="10" height="7.5" rx="1.8" fill="currentColor" />
                <path d="M5.25 7V5.25a2.75 2.75 0 0 1 5.5 0V7" fill="none" stroke="currentColor" strokeWidth="1.7" />
              </svg>
              <span>{t('checkout.securePayment')}</span>
            </div>
          </div>

          <div className="bp-card__body">{children}</div>

          <div className="bp-card__footer">
            {t('checkout.poweredBy')}
            {merchant?.supportEmail ? (
              <>
                {' · '}
                <a href={`mailto:${merchant.supportEmail}`}>{t('checkout.contactSupport')}</a>
              </>
            ) : null}

            <div className="bp-card__signature">
              <BarahWordmark height={13} />
            </div>
          </div>
        </div>

        <div className="bp-lang">
          {SUPPORTED_LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              onClick={() => switchLanguage(language)}
              aria-pressed={i18n.language.startsWith(language)}
            >
              {language === 'ar' ? 'العربية' : 'English'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
